/**
 * OTP Verification Page
 *
 * Support page for verifying OTP and managing support sessions.
 * Used by SUPPORT, ADMIN, and SADMIN roles.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Shield,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Loader2,
  ExternalLink,
} from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
} from '@/components/common';
import { organizationService } from '@/api/services';
import { useToast } from '@/hooks/useToast';
import { getSupportSessionBaseUrl } from '@/config/app.config';

// ============================================
// OTP Input Component
// ============================================

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
}

function OtpInput({ value, onChange, length = 6, disabled = false }: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, char: string) => {
    if (!/^\d*$/.test(char)) return;

    const newValue = value.split('');
    newValue[index] = char;
    const result = newValue.join('').slice(0, length);
    onChange(result);

    // Move to next input
    if (char && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-bold border border-border dark:border-border-dark rounded-lg bg-background dark:bg-background-dark text-foreground dark:text-foreground-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
}

// ============================================
// Page Component
// ============================================

export function OtpVerificationPage() {
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [orgName, setOrgName] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch organization and user details
  useEffect(() => {
    const fetchDetails = async () => {
      if (!orgId) return;

      setIsLoading(true);
      try {
        const org = await organizationService.getOrganizationById(orgId);
        setOrgName(org.name);
        // User name would typically come from another API call
        setUserName(`User ${userId?.substring(0, 8)}...`);
      } catch {
        toast.error('Failed to load details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, userId]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
    return undefined;
  }, [countdown]);

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await organizationService.verifySupportSessionOTP({
        orgId: orgId!,
        userId: userId!,
        otp,
      });

      if (response.isOtpVerified && response.sessionId) {
        setIsVerified(true);
        setSessionId(response.sessionId);
        toast.success('OTP verified successfully');

        // Automatically open support session in new window
        const sessionUrl = getSupportSessionBaseUrl();
        window.open(`${sessionUrl}?sessionId=${response.sessionId}`, '_blank');
      } else {
        setError('OTP verification failed. Please try again.');
        setOtp('');
      }
    } catch {
      setError('Invalid OTP. Please try again.');
      setOtp('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      // Simulate resend OTP API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('OTP sent successfully');
      setCountdown(60);
      setOtp('');
      setError(null);
    } catch {
      toast.error('Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  const handleOpenSession = () => {
    if (!sessionId) return;

    const sessionUrl = getSupportSessionBaseUrl();

    // Open the support session URL with sessionId in a new tab
    window.open(`${sessionUrl}?sessionId=${sessionId}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            OTP Verification
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Verify support session access for {orgName}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader description="Enter the OTP to verify your support session access">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                  Support Session Verification
                </span>
              </div>
            </CardHeader>
            <CardBody>
              {isVerified ? (
                // Success State
                <div className="text-center py-8">
                  <div className="flex justify-center mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                      <CheckCircle className="h-8 w-8 text-success" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground dark:text-foreground-dark mb-2">
                    Verification Successful
                  </h3>
                  <p className="text-foreground-muted dark:text-foreground-dark-muted mb-6">
                    You can now access the support session
                  </p>
                  <Button onClick={handleOpenSession}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Support Session
                  </Button>
                </div>
              ) : (
                // OTP Input State
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-foreground-muted dark:text-foreground-dark-muted mb-6">
                      Enter the 6-digit OTP sent to the user
                    </p>
                    <OtpInput
                      value={otp}
                      onChange={(value) => {
                        setOtp(value);
                        // Clear error when user starts typing
                        if (error) setError(null);
                      }}
                      disabled={isVerifying}
                    />
                    {error && (
                      <div className="flex items-center justify-center gap-2 mt-4 px-4 py-3 rounded-lg bg-danger/10 border border-danger/30">
                        <AlertCircle className="h-5 w-5 text-danger flex-shrink-0" />
                        <span className="text-sm font-medium text-danger">{error}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center gap-4">
                    <Button
                      onClick={handleVerify}
                      loading={isVerifying}
                      disabled={otp.length !== 6}
                      className="w-full sm:w-auto min-w-[200px]"
                    >
                      Verify OTP
                    </Button>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                        Didn't receive the OTP?
                      </span>
                      {countdown > 0 ? (
                        <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Resend in {countdown}s
                        </span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleResend}
                          loading={isResending}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Resend OTP
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Session Details */}
          <Card>
            <CardHeader title="Session Details" />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    Organization
                  </p>
                  <p className="font-medium text-foreground dark:text-foreground-dark">
                    {orgName || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    Organization ID
                  </p>
                  <p className="font-mono text-sm text-foreground dark:text-foreground-dark">
                    {orgId}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    User
                  </p>
                  <p className="font-medium text-foreground dark:text-foreground-dark">
                    {userName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    User ID
                  </p>
                  <p className="font-mono text-sm text-foreground dark:text-foreground-dark">
                    {userId}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Status Card */}
          <Card>
            <CardHeader title="Verification Status" />
            <CardBody>
              <div className="flex items-center gap-3">
                <Badge
                  variant={isVerified ? 'success' : 'warning'}
                  size="lg"
                  dot
                >
                  {isVerified ? 'Verified' : 'Pending Verification'}
                </Badge>
              </div>
            </CardBody>
          </Card>

          {/* Help Card */}
          <Card>
            <CardHeader title="Need Help?" />
            <CardBody>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                If you're having trouble with OTP verification, please ensure:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-foreground-muted dark:text-foreground-dark-muted list-disc list-inside">
                <li>The user has received the OTP</li>
                <li>You're entering the correct 6-digit code</li>
                <li>The OTP hasn't expired (valid for 5 minutes)</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default OtpVerificationPage;
